import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import AddUserModal from './AddUserModal';
import { getUsersPaged } from '../../../services/usersServices';
import EditCaseModal from './EditCaseModal';
import {
  getCaseById,
  getCreateInfo,
  removeUserFromCase,
  updateCase,
} from '../../../services/caseServices';
import {
  getServiceById,
  getServicesByBusinessLine,
  getServicesPaged,
} from '../../../services/servicesServices';
import { getClaveUnicaByClientId } from '../../../services/clientService';
import { getBusinessLinesPaged } from '../../../services/bussinesLineServices';
import { getAllCourts } from '../../../services/courtServices';
import { getCurrentUser } from 'services/authService';
import { getActiveInternalStates } from '../../../services/internalStatusServices';
import Message from '../../../components/Message/Message';
import './caseDetails.scss';
import DropdownAnchorItem from 'components/Dropdown/DropdownAnchorItem';
import { useLanguage } from '../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../utils/createScopedI18n';
import { caseDetailsI18n } from './i18n';
import usePermissionsCauses from 'permissions/modules/causes/usePermissionsCauses';

const CaseDetails = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const caseDetailsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: caseDetailsI18n });
  const navigate = useNavigate();
  const location = useLocation();

  const storedDailyData = localStorage.getItem('dailyCaseData');
  const initialCaseData =
    location.state?.caseData ||
    (storedDailyData ? JSON.parse(storedDailyData) : null);

  const [caseData, setCaseData] = useState(initialCaseData);
  const [userId, setUserId] = useState(null);

  const { id } = useParams();

  const [lawyers, setLawyers] = useState([]);

  const { Card, Badge, Button, Dropdown, Skeleton, IconButton, SvgIcon } =
    DesignSystemComponents;

  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditCase, setShowEditCase] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [usersTeam, setUsersTeam] = useState([]);
  const [group, setGroup] = useState([]);
  const [services, setServices] = useState([]);
  const [servicesByLine, setServicesByLine] = useState([]);
  const [courts, setCourts] = useState([]);
  const [sponsor, setSponsor] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const [activeInternalStates, setActiveInternalStates] = useState([]);

  const editCase = useMemo(
    () => [{ label: t('caseDetails_editCaseAction'), value: 'edit' }],
    [t]
  );
  const [flagPanel, setFlagPanel] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingClaveUnica, setLoadingClaveUnica] = useState(false);

  const handleToggleClaveUnica = async () => {
    if (showPassword) {
      setShowPassword(false);
      return;
    }
    const clientId = caseData?.client?.id;
    if (!clientId) return;
    try {
      setLoadingClaveUnica(true);
      const data = await getClaveUnicaByClientId(clientId);
      const value =
        typeof data === 'string'
          ? data
          : (data?.claveUnica ?? data?.value ?? '--');
      setCaseData((prev) =>
        prev?.client
          ? { ...prev, client: { ...prev.client, claveUnica: value } }
          : prev
      );
      setShowPassword(true);
    } catch (error) {
      console.error('Error fetching clave única:', error);
    } finally {
      setLoadingClaveUnica(false);
    }
  };

  const {
    canViewBlockedClientCause,
    canEdit,
    canViewClient,
    canAddUser,
    canRemoveUser,
    canViewLegacyMovements,
    canViewMovementsSection,
    canViewTasksSection,
  } = usePermissionsCauses();

  const canBypassCaseBlock = canViewBlockedClientCause;

  const tabs = useMemo(
    () => [
      {
        to: 'movimientos',
        label: t('caseDetails_tab_movements'),
        show: canViewMovementsSection,
      },
      {
        to: 'tareas',
        label: t('caseDetails_tab_tasks'),
        show: canViewTasksSection,
      },
      {
        to: 'escritos',
        label: t('caseDetails_tab_writings'),
        show: canViewTasksSection,
      },
      {
        to: 'movimientos-legacy',
        label: t('caseDetails_tab_oldMovements'),
        show: canViewLegacyMovements,
      },
    ],
    [t, canViewMovementsSection, canViewTasksSection, canViewLegacyMovements]
  );

  const clientHref = useMemo(() => {
    const id = caseData?.client?.id;
    if (!id) return null;

    const params = new URLSearchParams();
    if (flagPanel) params.set('flagPanel', '1');

    return `/crm/detalles-cliente/${id}?${params.toString()}`;
  }, [caseData?.client?.id, flagPanel]);

  const clientView = useMemo(() => {
    if (!clientHref) return [];
    return [
      {
        value: 'viewClient',
        label: (
          <DropdownAnchorItem href={clientHref}>
            {t('caseDetails_viewClient')}
          </DropdownAnchorItem>
        ),
      },
    ];
  }, [clientHref, t]);

  useEffect(() => {
    caseDetailsI18n.changeLanguage(caseDetailsLanguage);
  }, [caseDetailsLanguage]);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    const isBlocked = caseData?.internalState?.blockManager === true;
    setFlagPanel(isBlocked);
  }, [userId, caseData?.internalState?.blockManager]);

  useEffect(() => {
    if (!userId) return;
    const fetchData = async () => {
      await fetchCase();
      await fetchGroup();
      await fetchServicesAll();
      await fetchBusinessLines();
      await fetchCourts();
      await fetchActiveInternalStates();
    };
    fetchData();
  }, [userId]);

  useEffect(() => {
    if (!showEditCase) return;
    const activeBusinessLineId =
      caseData?.lineaNegocioId ?? caseData?.businessLineId ?? null;
    if (activeBusinessLineId == null) {
      setServicesByLine([]);
      return;
    }
    fetchServicesByLine(activeBusinessLineId);
  }, [showEditCase, caseData?.lineaNegocioId, caseData?.businessLineId]);

  useEffect(() => {
    return () => {
      localStorage.removeItem('dailyCaseData');
    };
  }, []);

  const fetchCase = async () => {
    try {
      setIsCaseLoading(true);
      const caseId = caseData?.id ?? Number(id);
      if (!caseId) return;
      const resp = await getCaseById(caseId);
      // setUsersTeam(resp.casesUsers.filter((ut) => !ut.deleted) || []);
      setUsersTeam(resp.casesUsers);
      setIsActive(
        String(resp.case.internalStateStatus ?? '')
          .trim()
          .toLowerCase() === 'activa'
      );

      setSponsor(resp.case.sponsor);

      setCaseData((prev) => ({
        ...prev,
        id: resp.case.id,
        title: resp.case.title,
        code: resp.case.code,
        court: resp.case.tribunal.nombre,
        courtId: resp.case.tribunalId,
        group: resp.case.businessArea?.name,
        businessAreaId: resp.case.businessAreaId,
        lineaNegocioId: resp.case.lineaNegocioId ?? resp.case.businessLineId,
        serviceId: resp.case.serviceId,
        active:
          String(resp.case.internalStateStatus ?? '')
            .trim()
            .toLowerCase() === 'activa',
        client: resp.case.client,
        civilCourtCode: resp.case.tribunal.codigoTribunalCivil,
        newCivilCourtCode: resp.case.tribunal.newCodigoTribunalCivil,
        internalStateId: resp.case.internalStateId,
        internalState: resp.case.internalState,
      }));
    } catch (error) {
      console.error('Error al obtener detalles de la causa:', error);
    } finally {
      setIsCaseLoading(false);
    }
  };

  const fetchGroup = async () => {
    try {
      const resp = await getCreateInfo();
      setGroup(resp.businessArea);
    } catch (error) {}
  };

  const fetchServicesAll = async () => {
    try {
      const resp = await getServicesPaged(1, 100, true, null, null, false);
      setServices(resp.services || []);
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]);
    }
  };

  const fetchServicesByLine = async (businessLineId) => {
    try {
      const resp = await getServicesByBusinessLine(businessLineId);
      setServicesByLine(Array.isArray(resp) ? resp : []);
    } catch (error) {
      console.error('Error loading services by business line:', error);
      setServicesByLine([]);
    }
  };

  const fetchCourts = async () => {
    const resp = await getAllCourts();
    setCourts(resp);
  };

  const fetchBusinessLines = async () => {
    try {
      const resp = await getBusinessLinesPaged(1, 100, true, null, null, false);
      setBusinessLines(resp.businessLines || []);
    } catch (error) {
      console.error('Error loading business lines:', error);
      setBusinessLines([]);
    }
  };

  const fetchActiveInternalStates = async () => {
    try {
      const resp = await getActiveInternalStates();
      setActiveInternalStates(Array.isArray(resp) ? resp : []);
    } catch (error) {
      console.error('Error loading internal states:', error);
      setActiveInternalStates([]);
    }
  };

  const handleSearchUser = async (query) => {
    try {
      const resp = await getUsersPaged(1, 20, false, query);

      const filtered = resp.users.filter(
        (u) => !usersTeam.some((t) => t.id === u.id)
      );

      setLawyers(filtered);
    } catch (error) {
      console.error('Error buscando usuarios:', error);
      setLawyers([]);
    }
  };

  const handleAddUser = async (userSelected) => {
    if (!userSelected?.id) return;

    setUsersTeam((prev) => {
      if (prev.some((u) => u.id === userSelected.id)) return prev;
      return [...prev, userSelected];
    });

    setCaseData((prev) => {
      const current = prev.newUsers ?? [];
      if (current.includes(userSelected.id)) return prev;

      return {
        ...prev,
        newUsers: [...current, userSelected.id],
      };
    });

    setShowAddUser(false);

    try {
      await handleEditCase({
        ...caseData,
        newUsers: [...(caseData.newUsers ?? []), userSelected.id],
      });
    } catch (error) {
      console.error('Error agregando usuario a la causa:', error);
    }
  };

  const handleEditCase = async (payload) => {
    try {
      const nextPayload = {
        ...payload,
        newUsers: payload.newUsers ?? caseData.newUsers ?? [],
      };

      const serviceId = nextPayload.serviceId ?? caseData?.serviceId ?? null;
      const hasLineaNegocio =
        nextPayload.lineaNegocioId != null && nextPayload.lineaNegocioId !== '';
      const currentLineaNegocioId =
        caseData?.lineaNegocioId ?? caseData?.businessLineId ?? null;

      if (!hasLineaNegocio && serviceId) {
        const serviceIdNumber = Number(serviceId);
        const serviceMatch = services.find(
          (service) => Number(service.id) === serviceIdNumber
        );
        if (serviceMatch?.businessLineId) {
          nextPayload.lineaNegocioId = serviceMatch.businessLineId;
        } else {
          const serviceInfo = await getServiceById(serviceIdNumber);
          nextPayload.lineaNegocioId = serviceInfo?.businessLineId ?? null;
        }
      }

      if (nextPayload.lineaNegocioId == null) {
        nextPayload.lineaNegocioId = currentLineaNegocioId;
      }

      const resp = await updateCase(nextPayload);

      setCaseData((prev) => ({
        ...prev,
        title: resp.case.title,
        code: resp.case.code,
        court: resp.case.tribunal.nombre,
        courtId: resp.case.tribunalId,
        businessAreaId: resp.case.businessAreaId,
        group: resp.case.businessArea?.name,
        lineaNegocioId:
          resp.case.lineaNegocioId ??
          resp.case.businessLineId ??
          nextPayload.lineaNegocioId,
        serviceId: resp.case.serviceId,
        active: resp.case.caseStateProcessId !== 3,
      }));
      setIsActive(
        String(resp.case.internalStateStatus ?? '')
          .trim()
          .toLowerCase() === 'activa'
      );
    } catch (error) {
      console.error('Error actualizando causa:', error);
    }
  };

  const handleBusinessLineChange = async (businessLineId) => {
    if (businessLineId == null || businessLineId === '') {
      setServicesByLine([]);
      return;
    }
    await fetchServicesByLine(businessLineId);
  };

  const handleRemoveUser = async (removedUserId) => {
    try {
      setUsersTeam((prev) => prev.filter((u) => u.id !== removedUserId));

      await removeUserFromCase(caseData.id, removedUserId, userId);

      setCaseData((prev) => {
        const updated = (prev.newUsers ?? []).filter(
          (id) => id !== removedUserId
        );
        return { ...prev, newUsers: updated };
      });
    } catch (error) {
      console.error('Error eliminando usuario:', error);
    }
  };

  useEffect(() => {
    const updateTableHeight = () => {
      const h = window.innerHeight;
      let maxHeight = 498;

      if (h < 600) {
        // Zoom 150%
        maxHeight = 60;
      } else if (h < 700) {
        // Zoom 125%
        maxHeight = 73;
      } else if (h < 800) {
        // Zoom 110%
        maxHeight = 75;
      } else if (h < 900) {
        // Zoom 100%
        maxHeight = 78;
      } else if (h < 1000) {
        // Zoom 90%
        maxHeight = 80;
      } else if (h < 1100) {
        // Zoom 80%
        maxHeight = 83;
      }

      document.documentElement.style.setProperty(
        '--info-max-height',
        `${maxHeight}%`
      );
    };

    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);

    return () => window.removeEventListener('resize', updateTableHeight);
  }, []);

  const caseBajaReason =
    caseData?.internalState?.description || t('caseDetails_noInfo');
  const caseInternalStateReason = (() => {
    const description = caseData?.internalState?.description;
    if (typeof description !== 'string') return null;
    const trimmed = description.trim();
    return trimmed ? trimmed : null;
  })();
  const hasPatrocinio = Boolean(caseData?.client?.patrocinio);

  return (
    <>
      {flagPanel && !canBypassCaseBlock ? (
        <div className='mt-4'>
          <Message
            type='warning'
            direction='horizontal'
            title={
              <>
                {t('caseDetails_blockedCaseTitle')}{' '}
                <span className='mini'></span>
              </>
            }
            description={
              <>
                {t('caseDetails_blockedCasePrefix')}{' '}
                <span className='fw-semibold'>
                  {caseData?.title || caseData?.code || '--'}
                </span>{' '}
                {t('caseDetails_blockedCaseSuffix')}
                <ul>
                  {' '}
                  <li>
                    <p className='fw-semibold gap-1 d-flex font-light'>
                      {t('caseDetails_clientLabel')}
                      <a
                        className='link'
                        href={`/crm/detalles-cliente/${caseData?.client?.id}`}
                      >
                        {caseData?.client?.name} ({t('caseDetails_clientRut')}{' '}
                        {caseData?.client?.identifier}
                        )
                        <SvgIcon
                          name='boxArrowUpRight'
                          size='.875rem'
                          fill='var(--primary-500)'
                        />
                      </a>
                    </p>
                  </li>
                  <li>
                    <li>
                      <p className=' d-flex gap-1'>
                        <span className='fw-semibold font-light '>
                          {t('caseDetails_role')}
                        </span>
                        {caseData?.code || '--'}
                      </p>
                    </li>
                    <li>
                      <p className=' d-flex gap-1'>
                        <span className='fw-semibold font-light '>
                          {t('caseDetails_court')}
                        </span>
                        {caseData?.court || '--'}
                      </p>
                    </li>
                    <p className=' d-flex gap-1'>
                      <span className='fw-semibold font-light '>
                        {t('caseDetails_reasonLabel')}
                      </span>
                      {caseBajaReason}
                    </p>
                  </li>
                </ul>
                <p className='fw-semibold mt-3'>
                  {t('caseDetails_nextStepsTitle')}
                </p>
                <ul className='list-auto'>
                  <li className='pt-1'>
                    {t('caseDetails_nextStepInformClient')}
                  </li>
                  <li className='pt-1'>
                    {t('caseDetails_nextStepTransferPrefix')}{' '}
                    <span className='fw-semibold'>
                      {t('caseDetails_nextStepTransferArea')}
                    </span>{' '}
                    {t('caseDetails_nextStepTransferSuffix')}
                  </li>
                </ul>
                {hasPatrocinio && (
                  <p className='mt-3 mb-0'>
                    {t('caseDetails_patrocinioWarning')}
                  </p>
                )}
              </>
            }
          >
            <Button
              variant='naked'
              size='sm'
              label={t('caseDetails_backToList')}
              iconStart='chevronLeft'
              onClick={() => navigate(-1)}
            />
          </Message>
        </div>
      ) : (
        <div>
          {flagPanel && canBypassCaseBlock && (
            <div className='warning-message'>{t('caseDetails_caseInDrop')}</div>
          )}
          <div className='d-flex align-items-center gap-2 mb-2'>
            <Button
              variant='naked'
              iconStart='chevronLeft'
              label={t('caseDetails_backToCases')}
              size='sm'
              onClick={() => navigate(-1)}
            />
          </div>

          <div className='mb-4'>
            <h2 className=' fw-semibold'>
              {`${t('caseDetails_title')} ${caseData?.title ?? ''} ${caseData?.code ?? ''}`}
            </h2>
            {hasPatrocinio && (
              <div className='warning-message mt-3'>
                {t('caseDetails_patrocinioWarning')}
              </div>
            )}
          </div>

          <div className='row g-4 case-details-layout'>
            {/*IZQUIERDA - ACTIVIDAD */}
            <div className='col-8'>
              {/* Movements, Tasks & Old Movements */}
              <Card
                specialPadding='p-0'
                id='caseActivity'
                className='activity-card'
              >
                <nav
                  role='tablist'
                  aria-label={t('caseDetails_tabsAriaLabel')}
                  style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
                  className='px-4 pt-4'
                >
                  {tabs.map(
                    (t) =>
                      t.show && (
                        <NavLink
                          key={t.to}
                          to={t.to}
                          role='tab'
                          className={({ isActive }) =>
                            'navbar-item ' + (isActive ? 'isActive' : '')
                          }
                          end
                        >
                          {({ isActive }) => (
                            <span aria-current={isActive ? 'page' : undefined}>
                              {t.label}
                            </span>
                          )}
                        </NavLink>
                      )
                  )}
                </nav>
                <div className='case-activity-scroll'>
                  <Outlet context={{ DesignSystemComponents, caseData }} />
                </div>
              </Card>
            </div>
            {/* DERECHA – INFO FIJA */}
            <div className='col-4'>
              <div className='case-info-sticky-wrap px-2'>
                <div className='case-info-scroll' id='caseInfo'>
                  <div className='row g-3'>
                    {/* Case */}
                    <div className='col-12'>
                      <Card specialPadding='p-4'>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                          <h3 className='caption fw-semibold mb-0'>
                            {t('caseDetails_caseInformation')}
                          </h3>
                          <div className='d-flex align-items-center gap-2'>
                            {isCaseLoading ? (
                              <Skeleton />
                            ) : (
                              <Badge
                                text={
                                  isActive
                                    ? t('caseDetails_activeStatus')
                                    : t('caseDetails_inactiveStatus')
                                }
                                color={isActive ? 'success' : 'error'}
                                size='sm'
                              />
                            )}
                            {canEdit && (
                              <Dropdown
                                ariaLabel={t('caseDetails_editCaseAriaLabel')}
                                icon='threeDotsVertical'
                                onSelect={() => setShowEditCase(true)}
                                options={editCase}
                                variant='icon'
                              />
                            )}
                          </div>
                        </div>

                        <div className='d-flex flex-column gap-2'>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_case')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.title || '--'}
                            </p>
                          </div>

                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_role')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.code || '--'}
                            </p>
                          </div>

                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_court')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.court || '--'}
                            </p>
                          </div>

                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_courtGroup')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.group || '--'}
                            </p>
                          </div>

                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_service')}
                            </p>
                            <p className='caption mb-0'>
                              {services.find(
                                (s) => s.id === caseData?.serviceId
                              )?.name || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_pjudProcessType')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.pjudProcessType || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_internalState')}
                            </p>
                            <p className='caption mb-0'>
                              {caseInternalStateReason || '--'}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>
                    {/* Client */}
                    <div className='col-12'>
                      <Card specialPadding='p-4'>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                          <h3 className='caption fw-semibold mb-0'>
                            {t('caseDetails_clientInformation')}
                          </h3>
                          {canViewClient && (
                            <Dropdown
                              ariaLabel={t(
                                'caseDetails_clientActionsAriaLabel'
                              )}
                              icon='threeDotsVertical'
                              options={clientView}
                              variant='icon'
                            />
                          )}
                        </div>

                        <div className='d-flex flex-column gap-2'>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_clientName')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.client?.name || '--'}
                            </p>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_clientIdentifier')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.client?.identifier || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_clientPhone')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.client?.telephone || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_password')}
                            </p>
                            <div className='d-flex justify-content-between align-items-center'>
                              <p className='caption mb-0'>
                                {showPassword
                                  ? caseData?.client?.claveUnica || '--'
                                  : '*****'}
                              </p>
                              <IconButton
                                ariaLabel={t(
                                  'caseDetails_viewPasswordAriaLabel'
                                )}
                                icon={showPassword ? 'eye' : 'eyeSlash'}
                                iconFill={
                                  !showPassword ? 'var(--dark)' : undefined
                                }
                                iconStroke={
                                  showPassword ? 'var(--dark)' : undefined
                                }
                                onClick={handleToggleClaveUnica}
                                disabled={loadingClaveUnica}
                                size='md'
                              />
                            </div>
                          </div>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_clientSAC')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.client?.userSac?.name || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='mini mb-1 font-light'>
                              {t('caseDetails_clientOffice')}
                            </p>
                            <p className='caption mb-0'>
                              {caseData?.client?.branch?.name || '--'}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>
                    {/* Team */}
                    <div className='col-12'>
                      <Card specialPadding='p-4'>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                          <h3 className='caption fw-semibold mb-0'>
                            {t('caseDetails_team')}
                          </h3>
                          <div className='d-flex align-items-center gap-2'>
                            <Button
                              label={t('caseDetails_addUser')}
                              size='sm'
                              variant='naked'
                              iconStart='plusLg'
                              onClick={() => setShowAddUser(true)}
                              disabled={!canAddUser}
                            />
                          </div>
                        </div>
                        <div className='d-flex flex-column gap-2'>
                          <p className='caption mb-0 font-light'>
                            {sponsor?.name} - {t('caseDetails_sponsorRole')}
                          </p>
                          {usersTeam.length > 0 ? (
                            usersTeam.map((user, i) => (
                              <div
                                key={i}
                                className='d-flex justify-content-between align-items-center mb-1'
                              >
                                <p className='caption mb-0 font-light'>
                                  {user.name} - {user.userTypeName}
                                </p>

                                <IconButton
                                  ariaLabel={t(
                                    'caseDetails_removeUserAriaLabel'
                                  )}
                                  icon='close'
                                  onClick={() => handleRemoveUser(user.id)}
                                  disabled={!canRemoveUser}
                                  size='sm'
                                />
                              </div>
                            ))
                          ) : (
                            <p className='mini mb-0 font-light'>
                              {t('caseDetails_noTeamUsers')}
                            </p>
                          )}
                        </div>
                      </Card>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <EditCaseModal
            DesignSystemComponents={DesignSystemComponents}
            show={showEditCase}
            caseData={caseData}
            courts={courts}
            group={group}
            services={servicesByLine}
            businessLines={businessLines}
            internalStates={activeInternalStates}
            onBusinessLineChange={handleBusinessLineChange}
            onClose={() => setShowEditCase(false)}
            onEdit={handleEditCase}
          />

          <AddUserModal
            DesignSystemComponents={DesignSystemComponents}
            show={showAddUser}
            onClose={() => setShowAddUser(false)}
            lawyers={lawyers}
            onSearch={handleSearchUser}
            onAdd={handleAddUser}
          />
        </div>
      )}
    </>
  );
};

export default CaseDetails;
